/**
 * Admin Dashboard Screen.
 * Shows key metrics and charts using Chart.js.
 */

import { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

import { PageHeader } from '@/app/PageHeader';
import { LoadingOverlay } from '@/app/Spinner';
import { classifyError } from '@/api/errors';
import * as dashboardApi from '@/api/dashboardApi';
import type { DashboardStats, DailyStats } from '@/api/types';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
);

export function DashboardScreen() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [dailyData, setDailyData] = useState<DailyStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [statsData, dailyStats] = await Promise.all([
          dashboardApi.getDashboardStats(),
          dashboardApi.getDailyStats(7),
        ]);
        setStats(statsData);
        // Backend may return either the array directly or a `{ data: [...] }`
        // envelope (depending on shape version). Normalize.
        setDailyData(
          Array.isArray(dailyStats)
            ? dailyStats
            : ((dailyStats as { data: DailyStats[] }).data ?? []),
        );
      } catch (err) {
        setError(classifyError(err).message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <LoadingOverlay />;

  if (error) {
    return (
      <div className="page-content">
        <PageHeader title="Dashboard" />
        <div className="alert alert--error">{error}</div>
      </div>
    );
  }

  if (!stats) return null;

  // Chart data for daily activity
  const dailyChartData = {
    labels: dailyData.map((d) =>
      new Date(d.date).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }),
    ),
    datasets: [
      {
        label: 'New Users',
        data: dailyData.map((d) => d.users_count),
        backgroundColor: 'rgba(31, 111, 235, 0.7)',
        borderRadius: 4,
      },
      {
        label: 'New Reports',
        data: dailyData.map((d) => d.reports_count),
        backgroundColor: 'rgba(26, 135, 84, 0.7)',
        borderRadius: 4,
      },
    ],
  };

  const dailyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' as const },
      title: { display: false },
    },
    scales: {
      y: { beginAtZero: true, ticks: { stepSize: 1 } },
    },
  };

  // Chart data for report status distribution
  const reportStatusData = {
    labels: ['Draft', 'Submitted', 'Archived'],
    datasets: [
      {
        data: [
          stats.reports_by_status.draft,
          stats.reports_by_status.submitted,
          stats.reports_by_status.archived,
        ],
        backgroundColor: ['#e7eef8', '#d6f0e1', '#ece7f5'],
        borderColor: ['#1f3e8c', '#1a6a3d', '#5a3aa1'],
        borderWidth: 1,
      },
    ],
  };

  // Chart data for subscription status distribution
  const subStatusData = {
    labels: ['Active', 'Trial', 'Cancelled'],
    datasets: [
      {
        data: [
          stats.subscriptions_by_status.active,
          stats.subscriptions_by_status.trial,
          stats.subscriptions_by_status.cancelled,
        ],
        backgroundColor: ['#d6f0e1', '#e7eef8', '#e0e0e0'],
        borderColor: ['#1a6a3d', '#1f3e8c', '#9e9e9e'],
        borderWidth: 1,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'right' as const },
    },
  };

  return (
    <div className="page-content">
      <PageHeader title="Dashboard" subtitle="Overview of FieldReport AI platform." />

      {/* Stats Grid */}
      <div className="stats-grid">
        <StatCard label="Total Users" value={stats.total_users} />
        <StatCard label="Total Reports" value={stats.total_reports} />
        <StatCard label="Total Subscriptions" value={stats.total_subscriptions} />
        <StatCard label="Reports Today" value={stats.active_reports_today} />
        <StatCard
          label="New Users (7d)"
          value={stats.new_users_this_week}
          trend={stats.new_users_this_week > 0 ? 'up' : undefined}
        />
        <StatCard
          label="New Reports (7d)"
          value={stats.new_reports_this_week}
          trend={stats.new_reports_this_week > 0 ? 'up' : undefined}
        />
      </div>

      {/* Charts */}
      <div className="grid-2 mb-24">
        <div className="card">
          <div className="card__header">
            <span className="card__title">Activity (Last 7 Days)</span>
          </div>
          <div className="card__body">
            <div className="chart-container">
              <Bar data={dailyChartData} options={dailyChartOptions} />
            </div>
          </div>
        </div>

        <div className="grid-2">
          <div className="card">
            <div className="card__header">
              <span className="card__title">Reports by Status</span>
            </div>
            <div className="card__body">
              <div className="chart-container">
                <Doughnut data={reportStatusData} options={doughnutOptions} />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card__header">
              <span className="card__title">Subscriptions by Status</span>
            </div>
            <div className="card__body">
              <div className="chart-container">
                <Doughnut data={subStatusData} options={doughnutOptions} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface StatCardProps {
  label: string;
  value: number;
  trend?: 'up' | 'down';
}

function StatCard({ label, value, trend }: StatCardProps) {
  return (
    <div className="stat-card">
      <div className="stat-card__label">{label}</div>
      <div className="stat-card__value">{value.toLocaleString()}</div>
      {trend && (
        <div className={`stat-card__trend stat-card__trend--${trend}`}>
          {trend === 'up' ? '↑ Growing' : '↓ Declining'}
        </div>
      )}
    </div>
  );
}
